import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import {
  instantDepuisHeureMurale,
  aujourdhuiBoutique,
  jourMural,
  type Creneau,
  type Horaires,
  type RendezVous,
} from '@iobiz/shared'
import { ChampDate } from '../../ui/ChampDate.js'
import { creneauxDuJour, deplacerRendezVous } from '../../data/repositories/agenda.local.js'

/**
 * Déplacer un rendez-vous.
 *
 * ⭐ Un geste distinct de l'annulation : le client vient un autre jour, il n'a
 * pas renoncé. Passer par « annuler puis reprendre » perdrait le lien avec la
 * vente, ferait deux traces là où il s'en est produit une, et laisserait la
 * boutique croire à un désistement.
 *
 * ⚠️ La durée est celle du RENDEZ-VOUS, relue de ses bornes — pas celle de
 * l'article aujourd'hui. Une prestation rallongée au catalogue ne doit pas
 * rallonger un rendez-vous déjà convenu avec quelqu'un.
 */
export function DeplacerRendezVousDialog({
  storeId,
  rdv,
  horaires,
  fuseau,
  onClose,
}: {
  storeId: string
  rdv: RendezVous
  horaires: Horaires
  fuseau: string
  onClose: () => void
}): JSX.Element {
  const duree = Math.round(
    (new Date(rdv.endsAt).getTime() - new Date(rdv.startsAt).getTime()) / 60000,
  )

  // Un rendez-vous passé se déplace vers AUJOURD'HUI au plus tôt : proposer
  // son jour d'origine ouvrirait sur une journée sans aucun créneau.
  const [date, setDate] = useState(() => {
    const origine = jourMural(rdv.startsAt, fuseau)
    const aujourdhui = aujourdhuiBoutique(fuseau)
    return origine < aujourdhui ? aujourdhui : origine
  })
  const [creneaux, setCreneaux] = useState<Creneau[]>([])
  const [choisi, setChoisi] = useState<Creneau | null>(null)
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    let vivant = true
    // Le rendez-vous DÉPLACÉ est ignoré dans les occupations : sinon il se
    // bloquerait lui-même et son propre horaire disparaîtrait des choix.
    void creneauxDuJour(storeId, date, horaires, duree, fuseau, rdv.id).then((c) => {
      if (vivant) {
        setCreneaux(c)
        setChoisi(null)
      }
    })
    return () => {
      vivant = false
    }
  }, [storeId, date, horaires, duree, fuseau, rdv.id])

  async function deplacer(): Promise<void> {
    if (!choisi) return
    setOccupe(true)
    setErreur(null)
    try {
      await deplacerRendezVous(
        storeId,
        rdv,
        instantDepuisHeureMurale(date, choisi.start, fuseau),
        instantDepuisHeureMurale(date, choisi.end, fuseau),
      )
      onClose()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Déplacement impossible.')
    } finally {
      setOccupe(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Déplacer le rendez-vous</DialogTitle>
      <DialogContent dividers>
        {erreur && (
          <Alert severity="error" sx={{ mb: 3 }} role="alert">
            {erreur}
          </Alert>
        )}

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          {rdv.label} — {rdv.customerName ?? 'client'}, {duree} min. Actuellement le{' '}
          {new Date(rdv.startsAt).toLocaleString('fr-FR', {
            day: 'numeric',
            month: 'long',
            hour: '2-digit',
            minute: '2-digit',
          })}
          .
        </Typography>

        <Box sx={{ display: 'grid', gap: 3 }}>
          <ChampDate
            label="Nouveau jour"
            value={date}
            onChange={setDate}
            // Aujourd'hui DE LA BOUTIQUE — voir la prise de rendez-vous.
            min={new Date(`${aujourdhuiBoutique(fuseau)}T00:00:00`)}
            jourFerme={(d) => horaires.days[d.getDay()]?.closed ?? false}
          />

          <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Créneaux disponibles
            </Typography>
            {creneaux.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                Rien de libre ce jour-là.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {creneaux.map((c) => (
                  <Chip
                    key={c.start}
                    label={c.start}
                    onClick={() => setChoisi(c)}
                    color={choisi?.start === c.start ? 'primary' : 'default'}
                    variant={choisi?.start === c.start ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={!choisi || occupe} onClick={() => void deplacer()}>
          Déplacer
        </Button>
      </DialogActions>
    </Dialog>
  )
}
